import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const areaData = {
  'Downtown Dubai': { current: 2850, growth: 22, yield: 5.8 },
  'Dubai Marina': { current: 1950, growth: 18, yield: 6.2 },
  'Palm Jumeirah': { current: 3200, growth: 25, yield: 5.1 },
  'Business Bay': { current: 1650, growth: 20, yield: 6.8 },
  'JVC': { current: 1100, growth: 15, yield: 7.5 },
  'JLT': { current: 1350, growth: 16, yield: 7.1 },
  'Yas Island (AD)': { current: 1450, growth: 19, yield: 6.5 },
  'Saadiyat (AD)': { current: 2100, growth: 17, yield: 5.9 }
};

const MarketAppreciation = () => {
  const [area, setArea] = useState('Downtown Dubai');
  const [sqft, setSqft] = useState(1000);
  const [years, setYears] = useState(5);

  const d = areaData?.[area];
  const currentValue = d?.current * sqft;
  const chartData = Array.from({ length: years + 1 }, (_, i) => ({
    year: `Y${i}`,
    value: Math.round(currentValue * Math.pow(1 + d?.growth / 100, i))
  }));
  const futureValue = chartData?.[chartData?.length - 1]?.value || 0;
  const gain = futureValue - currentValue;

  const fmt = (n) => 'AED ' + Math.round(n)?.toLocaleString();

  return (
    <div className="rounded-2xl p-6" style={{ background: 'rgba(10,22,40,0.9)', border: '1px solid rgba(0,212,255,0.2)' }}>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.3)' }}>
          <Icon name="TrendingUp" size={20} color="#00D4FF" />
        </div>
        <div>
          <h3 className="text-lg font-bold" style={{ color: '#E8F4FF' }}>Market Appreciation Calculator</h3>
          <p className="text-xs" style={{ color: '#6B9EC4' }}>Emirate-specific growth projections based on historical trends</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="text-xs mb-1 block" style={{ color: '#6B9EC4' }}>Area</label>
          <select value={area} onChange={e => setArea(e?.target?.value)}
            className="w-full px-3 py-2 rounded-xl text-sm outline-none"
            style={{ background: 'rgba(10,22,40,0.9)', border: '1px solid rgba(0,212,255,0.2)', color: '#E8F4FF' }}>
            {Object.keys(areaData)?.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs mb-1 block" style={{ color: '#6B9EC4' }}>Size (sq ft)</label>
          <input type="number" value={sqft} onChange={e => setSqft(+e?.target?.value)}
            className="w-full px-3 py-2 rounded-xl text-sm outline-none"
            style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.2)', color: '#E8F4FF' }} />
        </div>
        <div>
          <label className="text-xs mb-1 block" style={{ color: '#6B9EC4' }}>Projection: {years} years</label>
          <input type="range" min={1} max={10} value={years} onChange={e => setYears(+e?.target?.value)} className="w-full mt-2" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="p-3 rounded-xl text-center" style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)' }}>
          <div className="text-xs" style={{ color: '#6B9EC4' }}>Price/sqft</div>
          <div className="font-bold" style={{ color: '#00D4FF' }}>AED {d?.current}</div>
        </div>
        <div className="p-3 rounded-xl text-center" style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)' }}>
          <div className="text-xs" style={{ color: '#6B9EC4' }}>Annual Growth</div>
          <div className="font-bold" style={{ color: '#22c55e' }}>+{d?.growth}%</div>
        </div>
        <div className="p-3 rounded-xl text-center" style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)' }}>
          <div className="text-xs" style={{ color: '#6B9EC4' }}>Rental Yield</div>
          <div className="font-bold" style={{ color: '#f59e0b' }}>{d?.yield}%</div>
        </div>
      </div>
      <div className="h-40 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,212,255,0.1)" />
            <XAxis dataKey="year" tick={{ fill: '#6B9EC4', fontSize: 11 }} />
            <YAxis tick={{ fill: '#6B9EC4', fontSize: 10 }} tickFormatter={v => `${(v / 1000000)?.toFixed(1)}M`} />
            <Tooltip formatter={v => ['AED ' + v?.toLocaleString(), 'Value']} contentStyle={{ background: '#0A1628', border: '1px solid rgba(0,212,255,0.3)', borderRadius: 8, color: '#E8F4FF' }} />
            <Line type="monotone" dataKey="value" stroke="#00D4FF" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 rounded-xl" style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)' }}>
          <div className="text-xs" style={{ color: '#6B9EC4' }}>Current Value</div>
          <div className="font-bold text-sm" style={{ color: '#E8F4FF' }}>{fmt(currentValue)}</div>
        </div>
        <div className="p-3 rounded-xl" style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)' }}>
          <div className="text-xs" style={{ color: '#6B9EC4' }}>Value in {years} years</div>
          <div className="font-bold text-sm" style={{ color: '#22c55e' }}>{fmt(futureValue)}</div>
        </div>
      </div>
    </div>
  );
};

export default MarketAppreciation;
